"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePrenotaStore } from "@PN/inclusao";

type ParcelasBaseSnapshot = {
  baseCondicao: string;
  parcelasBaseProtheus: Record<string, string>;
  parcelasValorBaseProtheus: Record<string, number>;
  parcelasValorBaseProtheusNormalizado: Record<string, number>;
  parcelasValorBaseProtheusPorIndice: number[];
};

const parcelasBaseSnapshotCache = new Map<string, ParcelasBaseSnapshot>();

function getParcelasSignature(
  parcelas: Array<{ item?: string; vencimento?: string; valor?: number }>
): string {
  return parcelas
    .map((p) => `${p.item || ""}|${p.vencimento || ""}|${p.valor ?? 0}`)
    .join(";");
}

export function useParcelasValidation() {
  const draft = usePrenotaStore((state) => state.draft);
  const aux = usePrenotaStore((state) => state.aux);
  const { pagamentos, produtos, documento } = draft;
  const parcelasBaseProtheusRef = useRef<Record<string, string>>({});
  const parcelasValorBaseProtheusRef = useRef<Record<string, number>>({});
  const parcelasValorBaseProtheusNormalizadoRef = useRef<Record<string, number>>(
    {}
  );
  const parcelasValorBaseProtheusPorIndiceRef = useRef<number[]>([]);
  const baseCondicaoRef = useRef<string | undefined>(undefined);
  const lastSnapshotContextKeyRef = useRef<string>("");
  const shouldRefreshBaseFromCurrentParcelasRef = useRef(false);
  const parcelasSignatureAtContextChangeRef = useRef("");
  const snapshotContextKey = useMemo(
    () =>
      [
        documento.fornecedor || "",
        documento.loja || "",
        documento.dataEmissao || "",
        pagamentos.condicaoFinanceira || "",
      ].join("|"),
    [
      documento.fornecedor,
      documento.loja,
      documento.dataEmissao,
      pagamentos.condicaoFinanceira,
    ]
  );

  const normalizeItemKey = (item?: string) => {
    const trimmed = (item || "").trim();
    if (!trimmed) return "";
    const onlyDigits = trimmed.replace(/\D/g, "");
    if (!onlyDigits) return trimmed;
    const normalizedDigits = String(Number(onlyDigits));
    return normalizedDigits === "NaN" ? trimmed : normalizedDigits;
  };

  // Calcula valorNF usando fonte única da verdade
  const valorNF = useMemo(() => {
    // ✅ Usa o TOTAL da store (definitivamente SUBTOTAL/BRUTO)
    if (
      aux.totalNota &&
      typeof aux.totalNota === "object" &&
      (aux.totalNota as any).total
    ) {
      const totalStr = String((aux.totalNota as any).total || "0");
      const totalNum = Number(totalStr.replace(",", ".")) || 0;
      return totalNum;
    }

    // Fallback: calcula apenas dos produtos do draft (sem duplicação)
    return produtos.reduce((sum, p) => sum + (p.valorTotal || 0), 0);
  }, [produtos, aux.totalNota]);

  // Verifica se pode buscar parcelas
  const canFetch = useMemo(() => {
    return Boolean(
      valorNF > 0 &&
        pagamentos.condicaoFinanceira &&
        documento.dataEmissao &&
        documento.fornecedor &&
        documento.loja
    );
  }, [
    valorNF,
    pagamentos.condicaoFinanceira,
    documento.dataEmissao,
    documento.fornecedor,
    documento.loja,
  ]);

  // Parâmetros para consulta
  const params = useMemo(
    () => ({
      valor: valorNF,
      condpag: pagamentos.condicaoFinanceira,
      dtemissao: documento.dataEmissao,
      codForn: documento.fornecedor,
      lojaForn: documento.loja,
    }),
    [
      valorNF,
      pagamentos.condicaoFinanceira,
      documento.dataEmissao,
      documento.fornecedor,
      documento.loja,
    ]
  );

  // Mantém snapshot dos vencimentos originais vindos do Protheus.
  useEffect(() => {
    if (lastSnapshotContextKeyRef.current !== snapshotContextKey) {
      const cached = parcelasBaseSnapshotCache.get(snapshotContextKey);
      if (cached) {
        parcelasBaseProtheusRef.current = { ...cached.parcelasBaseProtheus };
        parcelasValorBaseProtheusRef.current = {
          ...cached.parcelasValorBaseProtheus,
        };
        parcelasValorBaseProtheusNormalizadoRef.current = {
          ...cached.parcelasValorBaseProtheusNormalizado,
        };
        parcelasValorBaseProtheusPorIndiceRef.current = [
          ...cached.parcelasValorBaseProtheusPorIndice,
        ];
        baseCondicaoRef.current = cached.baseCondicao;
        shouldRefreshBaseFromCurrentParcelasRef.current = false;
        parcelasSignatureAtContextChangeRef.current = "";
      } else {
        parcelasBaseProtheusRef.current = {};
        parcelasValorBaseProtheusRef.current = {};
        parcelasValorBaseProtheusNormalizadoRef.current = {};
        parcelasValorBaseProtheusPorIndiceRef.current = [];
        baseCondicaoRef.current = undefined;
        shouldRefreshBaseFromCurrentParcelasRef.current = true;
        parcelasSignatureAtContextChangeRef.current = getParcelasSignature(
          pagamentos.parcelas || []
        );
      }
      lastSnapshotContextKeyRef.current = snapshotContextKey;
      return;
    }

    const hasLocalSnapshot =
      Object.keys(parcelasBaseProtheusRef.current).length > 0 ||
      Object.keys(parcelasValorBaseProtheusRef.current).length > 0;
    if (hasLocalSnapshot) return;
    const cached = parcelasBaseSnapshotCache.get(snapshotContextKey);
    if (!cached) return;

    parcelasBaseProtheusRef.current = { ...cached.parcelasBaseProtheus };
    parcelasValorBaseProtheusRef.current = { ...cached.parcelasValorBaseProtheus };
    parcelasValorBaseProtheusNormalizadoRef.current = {
      ...cached.parcelasValorBaseProtheusNormalizado,
    };
    parcelasValorBaseProtheusPorIndiceRef.current = [
      ...cached.parcelasValorBaseProtheusPorIndice,
    ];
    baseCondicaoRef.current = cached.baseCondicao;
  }, [snapshotContextKey, pagamentos.parcelas]);

  useEffect(() => {
    if (!pagamentos.parcelas?.length) return;
    const condicaoAtual = pagamentos.condicaoFinanceira || "";
    if (shouldRefreshBaseFromCurrentParcelasRef.current) {
      const assinaturaAtual = getParcelasSignature(pagamentos.parcelas || []);
      if (assinaturaAtual === parcelasSignatureAtContextChangeRef.current) {
        // Ainda estamos com as parcelas do contexto anterior; aguarda retorno da condição atual.
        return;
      }
    }
    const hasSnapshot =
      Object.keys(parcelasBaseProtheusRef.current).length > 0 ||
      Object.keys(parcelasValorBaseProtheusRef.current).length > 0;
    const condicaoMudou = baseCondicaoRef.current !== condicaoAtual;

    // Evita que edições manuais atualizem a base original do Protheus.
    // A base é recapturada apenas quando ainda não existe snapshot ou quando a condição muda.
    if (hasSnapshot && !condicaoMudou) return;

    const nextBase: Record<string, string> = {};
    const nextValorBase: Record<string, number> = {};
    const nextValorBaseNormalizado: Record<string, number> = {};
    const nextValorBasePorIndice: number[] = [];
    pagamentos.parcelas.forEach((p) => {
      if (p.item && p.vencimento) {
        nextBase[p.item] = p.vencimento;
      }
      if (
        p.item &&
        typeof p.valor === "number" &&
        Number.isFinite(p.valor) &&
        p.valor >= 0
      ) {
        nextValorBase[p.item] = p.valor;
        nextValorBasePorIndice.push(p.valor);
        const normalized = normalizeItemKey(p.item);
        if (normalized && typeof nextValorBaseNormalizado[normalized] !== "number") {
          nextValorBaseNormalizado[normalized] = p.valor;
        }
      } else {
        nextValorBasePorIndice.push(0);
      }
    });

    if (Object.keys(nextBase).length > 0) {
      parcelasBaseProtheusRef.current = nextBase;
    }
    if (Object.keys(nextValorBase).length > 0) {
      parcelasValorBaseProtheusRef.current = nextValorBase;
    }
    if (Object.keys(nextValorBaseNormalizado).length > 0) {
      parcelasValorBaseProtheusNormalizadoRef.current = nextValorBaseNormalizado;
    }
    if (nextValorBasePorIndice.length > 0) {
      parcelasValorBaseProtheusPorIndiceRef.current = nextValorBasePorIndice;
    }
    baseCondicaoRef.current = condicaoAtual;
    shouldRefreshBaseFromCurrentParcelasRef.current = false;
    parcelasSignatureAtContextChangeRef.current = "";
    parcelasBaseSnapshotCache.set(snapshotContextKey, {
      baseCondicao: condicaoAtual,
      parcelasBaseProtheus: { ...parcelasBaseProtheusRef.current },
      parcelasValorBaseProtheus: { ...parcelasValorBaseProtheusRef.current },
      parcelasValorBaseProtheusNormalizado: {
        ...parcelasValorBaseProtheusNormalizadoRef.current,
      },
      parcelasValorBaseProtheusPorIndice: [
        ...parcelasValorBaseProtheusPorIndiceRef.current,
      ],
    });
  }, [
    pagamentos.parcelas,
    pagamentos.condicaoFinanceira,
    snapshotContextKey,
  ]);

  return {
    valorNF,
    canFetch,
    params,
    pagamentos,
    documento,
    parcelasBaseProtheus: parcelasBaseProtheusRef.current,
    parcelasValorBaseProtheus: parcelasValorBaseProtheusRef.current,
    parcelasValorBaseProtheusNormalizado:
      parcelasValorBaseProtheusNormalizadoRef.current,
    parcelasValorBaseProtheusPorIndice:
      parcelasValorBaseProtheusPorIndiceRef.current,
  };
}
